import { useCallback, useMemo, useRef, useState } from 'react'
import type { JSX } from 'react'
import { useNavigate } from 'react-router-dom'

import DOMPurify from 'dompurify'

import { Button, SuccessDialog } from '@buffer-mono/popcorn'

import { DEFAULT_COVER_IMAGE_URL } from './ChangelogEntryContent'

/** Reserve the cover's height before it loads so the modal doesn't jump. */
const COVER_ASPECT_RATIO = '16 / 9'

/** Inline-only formatting the authored summary may contain; everything else is stripped. */
const SUMMARY_SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'u', 'a', 'br', 'span'],
  ALLOWED_ATTR: ['href', 'target', 'rel'],
}

/**
 * Sanitizes the summary's inline HTML and forces `rel="noopener noreferrer"` on
 * any `target="_blank"` anchor, so an authored link that omits `rel` can't
 * reverse-tabnab. Done as a scoped post-process rather than a global
 * `DOMPurify.addHook` (which would affect every sanitize call app-wide).
 */
function sanitizeSummary(summary: string): string {
  const clean = DOMPurify.sanitize(summary, SUMMARY_SANITIZE_CONFIG)
  const template = document.createElement('template')
  template.innerHTML = clean
  template.content
    .querySelectorAll('a[target="_blank"]')
    .forEach((anchor) => anchor.setAttribute('rel', 'noopener noreferrer'))
  return template.innerHTML
}

export interface ChangelogAnnouncementCta {
  id: string
  label: string
  url: string
}

interface ChangelogAnnouncementModalProps {
  title: string
  /** Concise blurb; may contain inline formatting. Always present (server-resolved). */
  summary: string
  coverImageUrl: string | null
  /** Effective CTAs (already includes the default "Learn more"), resolved server-side. */
  ctas: readonly ChangelogAnnouncementCta[]
  /** Genuine close (X / Escape / outside-click): fires dismissed tracking + marks seen. */
  onDismiss: () => void
  /**
   * A CTA was clicked: fires CTA-click tracking. `opensExternally` is true for
   * external links (which open a new tab and leave the modal open — the caller
   * must not mark seen) and false for in-app CTAs (which navigate away, so the
   * caller marks seen).
   */
  onCtaClick: (cta: ChangelogAnnouncementCta, opensExternally: boolean) => void
}

/**
 * A CTA whose URL is an in-app route (SPA-navigable) rather than an external
 * link. Excludes protocol-relative URLs (`//host`) — those are cross-origin and
 * must take the external branch, not `navigate()` (which would throw on a
 * cross-origin history update).
 */
const isInternal = (url: string): boolean =>
  url.startsWith('/') && !url.startsWith('//')

/**
 * The interruptive announcement rendering of a changelog entry (`displayType:
 * modal`), built on Popcorn's `SuccessDialog` — the same component the Admin HQ
 * preview mirrors. Concise by design: cover → title → summary → CTAs.
 *
 * Dismissible via close button / Escape / outside-click (fires dismissed
 * tracking + marks seen), and by an in-app CTA which navigates away (fires
 * CTA-click tracking + marks seen). An external CTA fires CTA-click tracking,
 * opens a new tab, and deliberately leaves the modal open — marking it seen
 * there would tear the modal down (via the seen-refetch) when focus returns.
 */
export function ChangelogAnnouncementModal({
  title,
  summary,
  coverImageUrl,
  ctas,
  onDismiss,
  onCtaClick,
}: ChangelogAnnouncementModalProps): JSX.Element {
  const navigate = useNavigate()
  const [open, setOpen] = useState(true)
  const dismissedRef = useRef(false)
  const sanitizedSummary = useMemo(() => sanitizeSummary(summary), [summary])

  // Genuine close only (X / Escape / outside-click). Guarded so the dismissed
  // tracking + seen mutation run at most once.
  const dismiss = useCallback((): void => {
    if (dismissedRef.current) return
    dismissedRef.current = true
    setOpen(false)
    onDismiss()
  }, [onDismiss])

  const handleCtaClick = (cta: ChangelogAnnouncementCta): void => {
    if (isInternal(cta.url)) {
      onCtaClick(cta, false)
      setOpen(false)
      navigate(cta.url)
    } else {
      onCtaClick(cta, true)
      window.open(cta.url, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <SuccessDialog
      open={open}
      onOpenChange={(next): void => {
        if (!next) dismiss()
      }}
    >
      <SuccessDialog.Content size="medium">
        <SuccessDialog.Image
          src={coverImageUrl ?? DEFAULT_COVER_IMAGE_URL}
          alt=""
          style={{ aspectRatio: COVER_ASPECT_RATIO }}
        />
        <SuccessDialog.Title>{title || 'Untitled'}</SuccessDialog.Title>
        {/* Summary is inline-only HTML, valid inside the description's <p>. */}
        <SuccessDialog.Description
          dangerouslySetInnerHTML={{ __html: sanitizedSummary }}
        />
        <SuccessDialog.Actions>
          {ctas.map((cta, index) => (
            <Button
              key={cta.id}
              type="button"
              variant={index === 0 ? 'primary' : 'secondary'}
              size="large"
              onClick={(): void => handleCtaClick(cta)}
            >
              {cta.label}
            </Button>
          ))}
        </SuccessDialog.Actions>
      </SuccessDialog.Content>
    </SuccessDialog>
  )
}
